import { GenericApiResponse } from '@api/types/genertic-api-response';
import { capitalizeFirst, sentenceCase } from '@app/utils/capilatizeFirst';
import { AxiosInstance, AxiosRequestConfig } from 'axios';

const RAW_TEXT = '__rawText__' as const;

// For fetches that seed an edit form: the form must load and save the stored value,
// not the display-cased one, or a save rewrites the record.
export const rawText = <T extends AxiosRequestConfig>(config: T = {} as T): T =>
  ({
    ...config,
    [RAW_TEXT]: true,
  }) as T;

// Display-cases data fetched with rawText(), exactly as the interceptor would have.
export const displayCased = <T>(data: T): T => processObjectWith(data, false) as T;

export const setupResponseInterceptor = (clientKey: string, axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => {
      if ((response.config as { [RAW_TEXT]?: boolean } | undefined)?.[RAW_TEXT]) return response;
      response.data = processResponse(response.data, isProviderScoped(response.config?.params));
      return response;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

// Billing lists filtered to providers (`type=provider`) return rows that don't say so themselves.
function isProviderScoped(params: unknown): boolean {
  if (!params || typeof params !== 'object') return false;
  const { type, entityType } = params as Record<string, unknown>;
  return type === 'provider' || entityType === 'provider';
}

function processResponse(responseData: unknown, providerScoped = false): unknown {
  const processObject = (obj: unknown) => processObjectWith(obj, providerScoped);

  // Handle null/undefined responses
  if (!responseData) {
    return responseData;
  }

  // File downloads: spreading a Blob would turn it into {}.
  if (responseData instanceof Blob || responseData instanceof ArrayBuffer) {
    return responseData;
  }

  // Handle GenericApiResponse structure
  if (isGenericApiResponse(responseData)) {
    const hasDataAndArray = responseData.data && Array.isArray(responseData.data);

    if (hasDataAndArray) {
      responseData.data = (responseData.data as object[]).map((item) => processObject(item) as object);
    } else if (responseData.data && typeof responseData.data === 'object') {
      responseData.data = processObject(responseData.data) as object;
    }

    return responseData;
  }

  // Handle direct array responses
  if (Array.isArray(responseData)) {
    return responseData.map((item) => processObject(item));
  }

  // Handle direct object responses
  if (typeof responseData === 'object') {
    return processObject(responseData);
  }

  return responseData;
}

// Type guard to check if response follows GenericApiResponse structure
function isGenericApiResponse(data: unknown): data is GenericApiResponse {
  return Boolean(data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, 'data'));
}

// Words/fields to capitalize
const capitalizeWords = [
  'firstname',
  'lastname',
  'city',
  'country',
  'address',
  'state',
  'street',
  'name',
  'title',
  'description',
  'entityname',
  'clientname',
  'partnername',
];

// Free-text fields get sentence case, not Title Case Every Word.
const sentenceKeys = ['title', 'description'];

// Provider names are organisation names ("CT Lab", "ΙΑΤΡΙΚΟ ΚΕΝΤΡΟ") and are shown exactly as stored.
function verbatimKeys(obj: Record<string, unknown>, providerScoped: boolean): string[] {
  const keys: string[] = [];
  if ('externalProviderId' in obj) keys.push('name');
  if (providerScoped || obj.type === 'provider' || obj.entityType === 'provider') {
    keys.push('entityname', 'partnername');
  }
  return keys;
}

function processObjectWith(obj: unknown, providerScoped: boolean): unknown {
  const processObject = (value: unknown) => processObjectWith(value, providerScoped);
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  // Handle arrays within objects
  if (Array.isArray(obj)) {
    return obj.map(processObject);
  }

  const newObj = { ...obj } as Record<string, unknown>;
  const keepAsIs = verbatimKeys(newObj, providerScoped);

  // Process all properties recursively
  Object.keys(newObj).forEach((key) => {
    const value = newObj[key];

    // Recursively process nested objects/arrays
    if (Array.isArray(value)) {
      newObj[key] = value.map(processObject);
    } else if (value && typeof value === 'object') {
      newObj[key] = processObject(value);
    } else if (
      capitalizeWords.includes(key.toLowerCase()) &&
      !keepAsIs.includes(key.toLowerCase()) &&
      typeof value === 'string'
    ) {
      // Capitalize specific fields
      newObj[key] = sentenceKeys.includes(key.toLowerCase()) ? sentenceCase(value) : capitalizeFirst(value);
    }
  });

  return newObj;
}
